import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Badge from '@/components/ui/Badge';

/**
 * AdminLayout
 * Layout for admin-only back-office screens
 * Includes admin sidebar navigation with admin badge
 */
export default function AdminLayout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navItems = [
    { path: '/admin', label: 'Tableau de bord', icon: '📊' },
    { path: '/admin/quizzes', label: 'Quiz', icon: '📝' },
    { path: '/admin/users', label: 'Utilisateurs', icon: '👥' },
    { path: '/admin/themes', label: 'Thèmes', icon: '🏷️' },
    { path: '/admin/reports', label: 'Signalements', icon: '🚩' }
  ];

  const isActive = (path) =>
    path === '/admin' ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen flex bg-neutral-50">
      {/* Sidebar */}
      <aside className="w-64 bg-neutral-900 text-white flex flex-col">
        {/* Brand with admin badge */}
        <div className="p-6 border-b border-neutral-700">
          <Link to="/admin" className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/animfacile.png" alt="AnimFacile logo" className="h-10 w-auto" />
              <span className="text-xl font-bold text-white">AnimFacile</span>
            </div>
            <Badge variant="warning" size="sm">
              Admin
            </Badge>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1" aria-label="Navigation admin">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={[
                'flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors',
                isActive(item.path)
                  ? 'bg-neutral-700 text-white font-medium'
                  : 'text-neutral-300 hover:bg-neutral-800'
              ].join(' ')}
              aria-current={isActive(item.path) ? 'page' : undefined}
            >
              <span aria-hidden="true" className="text-xl">
                {item.icon}
              </span>
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Back to host area */}
        <div className="p-4 border-t border-neutral-700">
          <Link
            to="/dashboard"
            className="block px-4 py-3 text-sm text-neutral-300 hover:bg-neutral-800 rounded-lg transition-colors"
          >
            {"← Retour à l'interface hôte"}
          </Link>
        </div>

        {/* User section */}
        <div className="p-4 border-t border-neutral-700">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-neutral-700 flex items-center justify-center text-white font-bold">
              {user?.email?.[0]?.toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.displayName || user?.email}
              </p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-neutral-800 rounded-lg transition-colors"
          >
            Se déconnecter
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <header className="bg-white border-b border-neutral-200 h-16 flex items-center justify-between px-6">
          <h1 className="text-lg font-semibold text-neutral-900">
            {navItems.find((item) => isActive(item.path))?.label || 'Administration'}
          </h1>
        </header>

        {/* Page content */}
        <main role="main" className="flex-1 p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
